/**
 * Classifies ArTable rows as bundle headers, bundle members, or ungrouped.
 *
 * Two passes in document order with no reordering: the first indexes every header
 * by bundleId, the second resolves each member against that index. Membership is
 * therefore independent of adjacency — rows arrive in Firestore query order, and a
 * column or system sort can interleave bundles or place a member ahead of its own
 * header, so a positional walk misclassifies genuine members as ungrouped.
 *
 * A member whose header is absent from the row set — off-page, filtered out, or
 * outside a PDF/selection subset — stays a member with an unresolved `bundleType`.
 * Consumers must fail closed on that, since a misclassified member escapes every
 * member-level visibility rule.
 *
 * Consumed by `useArTableBundleRows` (screen and PDF render plan), by
 * `ArTableBody`'s `flatMemberBundleTypes` (member identity once bundles are
 * disabled on the table type) and by `resolveArTableBundleExportVisibility`
 * (both XLSX producers). They must agree row for row, so the walk lives here
 * rather than inside the hook. The XLSX callers classify after split and column
 * filtering and before the ticked selection narrows the list; `ArTableBody` in
 * PDF mode classifies the selection-narrowed list, so an unticked-header member
 * there fails closed instead of resolving (SYD-2279).
 */

import type { ArBundleType, ArRowData, ArRowId } from '#types/System.Artifacts.types';
import type { DocEntry } from '../../store/Store.types';

type ArBundleRowClass =
  | { kind: 'bundleHeader'; rowId: ArRowId; bundleId: string; bundleType: ArBundleType }
  | { kind: 'bundleMember'; rowId: ArRowId; bundleId: string; bundleType: ArBundleType | undefined }
  | { kind: 'ungrouped'; rowId: ArRowId };

type ArBundleRowClassification = {
  items: ArBundleRowClass[];
  hasBundles: boolean;
};

function classifyArBundleRows(params: {
  rowIds: readonly ArRowId[];
  rowsDocs: ReadonlyMap<string, DocEntry<ArRowData>>;
}): ArBundleRowClassification {
  const items: ArBundleRowClass[] = [];

  const bundleTypeByBundleId = new Map<string, ArBundleType>();
  for (const rowId of params.rowIds) {
    const data = params.rowsDocs.get(rowId)?.data;
    if (!data || data.bundleRole !== 'header' || !data.bundleId) continue;
    if (bundleTypeByBundleId.has(data.bundleId)) continue;
    bundleTypeByBundleId.set(data.bundleId, data.bundleType ?? 'group');
  }

  const hasBundles = bundleTypeByBundleId.size > 0
    || params.rowIds.some((rowId) => {
      const data = params.rowsDocs.get(rowId)?.data;
      return data?.bundleRole === 'member' && Boolean(data.bundleId);
    });

  for (const rowId of params.rowIds) {
    const data = params.rowsDocs.get(rowId)?.data;
    if (!data) continue;

    if (data.bundleRole === 'header' && data.bundleId) {
      items.push({
        kind: 'bundleHeader',
        rowId,
        bundleId: data.bundleId,
        bundleType: bundleTypeByBundleId.get(data.bundleId) ?? 'group',
      });
      continue;
    }

    if (data.bundleRole === 'member' && data.bundleId) {
      items.push({
        kind: 'bundleMember',
        rowId,
        bundleId: data.bundleId,
        bundleType: bundleTypeByBundleId.get(data.bundleId),
      });
      continue;
    }

    items.push({ kind: 'ungrouped', rowId });
  }

  return { items, hasBundles };
}

export { classifyArBundleRows };
export type { ArBundleRowClass, ArBundleRowClassification };
